import React, { useState, useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { validatePassword } from '../../../helpers';
import { handleUserRedirect } from '../../../utils/userRedirect';
import RequestButton from '../../ui_kit/utilities/request_button';
import Context from '../../../utils/context';

import styles from './index.module.css';

const EyeOpenIcon = () => (
  <svg width='20' height='14' viewBox='0 0 22 16' fill='none' aria-hidden='true'>
    <path
      d='M19.87 6.96c.474.62.474 1.46 0 2.08C18.377 10.987 14.795 15 10.613 15S2.85 10.987 1.356 9.04A1.66 1.66 0 0 1 1 8c0-.376.125-.74.356-1.038C2.85 5.013 6.43 1 10.613 1s7.764 4.013 9.257 5.96Z'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <circle cx='10.613' cy='8' r='3' stroke='currentColor' strokeWidth='1.6' />
  </svg>
);

const EyeOffIcon = () => (
  <svg width='20' height='16' viewBox='0 0 22 18' fill='none' aria-hidden='true'>
    <path
      d='M5.486 14.13C3.64 12.819 2.18 11.115 1.356 10.04A1.66 1.66 0 0 1 1 9c0-.376.125-.74.356-1.038C2.85 6.013 6.43 2 10.613 2c1.876 0 3.63.807 5.13 1.874'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M12.74 6.887a3 3 0 0 0-4.241 4.243M2.613 17 18.613 1M8.613 15.704c.65.193 1.323.293 2 .296 4.182 0 7.764-4.013 9.257-5.962a1.66 1.66 0 0 0 .002-2.078A24.5 24.5 0 0 0 18.183 6'
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

export default function UsersPasswordReset() {
  const context = useContext(Context);
  const history = useHistory();
  const location = useLocation();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');

  const searchParams = new URLSearchParams(location.search);
  const returnTo = searchParams.get('return_to') || '';
  const isInvite = searchParams.get('invite') === 'true';

  const validPassword = validatePassword(password);
  const passwordsMatch = password !== '' && password === confirmPassword;
  const canSubmit = validPassword && passwordsMatch;

  const validateOnBlur = () => {
    if (password !== '' && !validPassword) {
      setError(
        'Password must have 8 characters, include upper and lower case letters, numbers, and 1 special character ( / $ % @ ).'
      );
    } else if (password !== '' && confirmPassword !== '' && !passwordsMatch) {
      setError('The passwords you entered do not match');
    } else {
      setError('');
    }
  };

  const submit = (afterAction) => {
    if (!canSubmit) {
      validateOnBlur();
      afterAction();
      return;
    }

    setError('');
    context.auth.userResetPassword(
      gon.reset_password_token,
      password,
      (user) => {
        afterAction();
        handleUserRedirect(user, history, { isImpersonation: false, returnTo });
      },
      (data) => {
        afterAction();
        if (data.status === '2fa_required') {
          history.push('/auth/2fa');
        }
      },
      (errors) => {
        afterAction();
        setError(errors.join(','));
      }
    );
  };

  return (
    <div className={styles.auth_page}>
      <div className={styles.auth_page_content}>
        <div className={styles.auth_card}>
          <div className={styles.auth_card_header}>
            <h1 className={styles.auth_heading}>{isInvite ? 'Welcome to PatientPartner!' : 'Create a new password'}</h1>
            <p className={styles.auth_subtitle}>
              {isInvite
                ? 'Create a password to finish setting up your account.'
                : 'Enter a new password for your account.'}
            </p>
          </div>

          {error && <p className={styles.auth_error}>{error}</p>}

          <div className={styles.auth_field}>
            <label className={styles.auth_field_label} htmlFor='new-password'>
              New password
            </label>
            <div className={styles.auth_input_wrap}>
              <input
                id='new-password'
                type={showPassword ? 'text' : 'password'}
                name='password'
                autoComplete='new-password'
                className={`${styles.auth_input} fs-mask`}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={validateOnBlur}
                placeholder='••••••••'
                required
              />
              <button
                type='button'
                className={styles.auth_input_toggle}
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOpenIcon /> : <EyeOffIcon />}
              </button>
            </div>
          </div>

          <div className={styles.auth_field}>
            <label className={styles.auth_field_label} htmlFor='confirm-password'>
              Confirm new password
            </label>
            <div className={styles.auth_input_wrap}>
              <input
                id='confirm-password'
                type={showConfirmPassword ? 'text' : 'password'}
                name='confirm-password'
                autoComplete='new-password'
                className={`${styles.auth_input} fs-mask`}
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onBlur={validateOnBlur}
                placeholder='••••••••'
                required
              />
              <button
                type='button'
                className={styles.auth_input_toggle}
                onClick={() => setShowConfirmPassword((value) => !value)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? <EyeOpenIcon /> : <EyeOffIcon />}
              </button>
            </div>
          </div>

          <p className={styles.auth_hint}>
            Must be at least 8 characters and include upper and lower case letters, a number, and a special character (
            / $ % @ ).
          </p>

          <RequestButton
            request={submit}
            recaptcha={true}
            disabled={canSubmit ? '' : 'disabled'}
            classnames={styles.auth_button}
            cta={isInvite ? 'Set password' : 'Create new password'}
          />
        </div>
      </div>
    </div>
  );
}
